/**
 * `<AddColumnModal>` — picker for adding a user-defined column to the Trackers
 * board. Column types are grouped into a left-hand rail; selecting a group
 * swaps the grid of type cards on the right. Clicking a type immediately
 * creates the column with an auto-incremented default name (handled by the
 * consumer) and closes the modal.
 *
 * Adding a column type is a one-line change to `COLUMN_GROUPS` — the rail, its
 * count and the grid all read from that catalogue.
 */
import { useState } from 'react';
import { LayoutGrid, Plus } from 'lucide-react';
import { FormDialog } from '@/shared/ui/FormDialog';
import { SearchInput } from '@/shared/ui/SearchInput';
import { useAssetApprovalsHidden } from '@/shared/hooks/useAssetApprovalsHidden';
import {
  APPROVAL_COLUMN_TYPES,
  COLUMN_GROUPS,
  type ColumnGroup,
  type ColumnTypeOption,
} from '../../lib/column-catalog';
import type { CustomColumnType } from '../../lib/useTrackersBoard';
import { useTranslation } from 'react-i18next';
import type { ParseKeys } from 'i18next';

interface AddColumnModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (type: CustomColumnType) => void;
  /**
   * Column types to hide from the picker. Groups left with zero options
   * disappear from the rail entirely — e.g. custom trackers pass the
   * brief-bound types (`brief_type`, `project`, `assets`) so the whole "Briefs"
   * group vanishes for them.
   */
  excludeTypes?: readonly CustomColumnType[];
}

/** Rail entry that shows the whole catalogue rather than one group. */
// eslint-disable-next-line i18next/no-literal-string -- a selection key compared against group ids, never rendered
const ALL_GROUP_ID = 'all';

function visibleGroups(
  groups: readonly ColumnGroup[],
  excludeTypes: readonly CustomColumnType[] | undefined,
): ColumnGroup[] {
  return groups
    .map((group) => ({
      ...group,
      options: excludeTypes
        ? group.options.filter((o) => !excludeTypes.includes(o.type))
        : group.options,
    }))
    .filter((group) => group.options.length > 0);
}

export function AddColumnModal({ open, onOpenChange, onAdd, excludeTypes }: AddColumnModalProps) {
  const { t } = useTranslation('trackers');
  const assetApprovalsHidden = useAssetApprovalsHidden();

  // `hide_asset_approvals` drops the approval column types from the picker via
  // the same `excludeTypes` seam brief-bound callers already use.
  const effectiveExclude = assetApprovalsHidden
    ? [...(excludeTypes ?? []), ...APPROVAL_COLUMN_TYPES]
    : excludeTypes;
  const groups = visibleGroups(COLUMN_GROUPS, effectiveExclude);

  const add = (type: CustomColumnType) => {
    onAdd(type);
    onOpenChange(false);
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('trackersBoard.addColumnModal.addColumn')}
      description={t('trackersBoard.addColumnModal.chooseWhatKindOfData')}
      // DialogContent pins a fixed width (`w-[min(100vw-2rem,40rem)]`), so a
      // larger max-width alone does nothing — override the width itself.
      className="w-[92vw] sm:max-w-[680px]"
    >
      <FormDialog.Body>
        <ColumnTypePicker groups={groups} onAdd={add} />
      </FormDialog.Body>
    </FormDialog>
  );
}

interface PickerProps {
  groups: ColumnGroup[];
  onAdd: (type: CustomColumnType) => void;
}

function ColumnTypePicker({ groups, onAdd }: PickerProps) {
  const { t } = useTranslation('trackers');

  const [activeGroupId, setActiveGroupId] = useState(ALL_GROUP_ID);
  const [query, setQuery] = useState('');

  const allOptions = groups.flatMap((g) => g.options);
  // The rail counts the catalogue, not the search: they tell the user how big
  // each group is, and a count that shrank as they typed would read as the
  // group itself having lost types.
  const railEntries = [
    {
      id: ALL_GROUP_ID,
      labelKey: 'trackersBoard.addColumnModal.allColumns' as ParseKeys<'trackers'>,
      Icon: LayoutGrid,
      count: allOptions.length,
    },
    ...groups.map((g) => ({
      id: g.id,
      labelKey: g.labelKey,
      Icon: g.Icon,
      count: g.options.length,
    })),
  ];

  const q = query.trim().toLowerCase();
  const matches = (group: ColumnGroup, option: ColumnTypeOption) =>
    !q ||
    t(option.labelKey).toLowerCase().includes(q) ||
    t(group.labelKey).toLowerCase().includes(q);

  const options = groups
    .filter((g) => activeGroupId === ALL_GROUP_ID || g.id === activeGroupId)
    .flatMap((g) => g.options.filter((o) => matches(g, o)));

  return (
    <div className="flex flex-col gap-3 pt-1">
      <SearchInput
        aria-label={t('trackersBoard.addColumnModal.searchColumnTypesLabel')}
        placeholder={t('trackersBoard.addColumnModal.searchColumnTypes')}
        value={query}
        onChange={setQuery}
      />

      <div className="flex items-stretch gap-4">
        <nav className="flex w-44 shrink-0 flex-col gap-0.5 border-r border-border pr-4">
          {railEntries.map(({ id, labelKey, Icon, count }) => (
            <button
              key={id}
              type="button"
              onClick={() => setActiveGroupId(id)}
              aria-current={id === activeGroupId}
              className={`flex items-center gap-2 rounded-md px-2.5 py-2 text-left text-[13px] leading-[18px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                id === activeGroupId
                  ? 'bg-accent font-medium text-foreground'
                  : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground'
              }`}
            >
              <Icon className="size-4 shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate">{t(labelKey)}</span>
              <span className="text-xs text-muted-foreground">{count}</span>
            </button>
          ))}
        </nav>

        {/* Fixed height so the modal never resizes as the user switches groups
            or narrows the search — the card grid scrolls instead. */}
        <div className="grid h-[min(520px,62vh)] flex-1 auto-rows-min grid-cols-2 content-start gap-2 overflow-y-auto pr-3">
          {options.length === 0 ? (
            <p className="col-span-2 py-8 text-center text-sm text-muted-foreground">
              {t('trackersBoard.addColumnModal.noColumnTypesMatch')}
            </p>
          ) : (
            options.map(({ type, labelKey, descriptionKey, Icon }) => (
              <button
                key={type}
                type="button"
                onClick={() => onAdd(type)}
                className="flex items-start gap-3 rounded-lg border border-border bg-card p-3 text-left transition-colors hover:border-border-strong hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-accent">
                  <Icon className="size-4 text-foreground" aria-hidden="true" />
                </span>
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-sm font-medium text-foreground">{t(labelKey)}</span>
                  <span className="text-xs text-pretty text-muted-foreground">
                    {t(descriptionKey)}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      </div>

      {/* The prototype's group authoring has no model behind it yet — no way to
          name a group or move types between them — so the affordance ships
          inert rather than pretending to work. */}
      <button
        type="button"
        disabled
        className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border-strong p-3.5 text-sm text-muted-foreground"
      >
        <Plus className="size-4" aria-hidden="true" />
        {t('trackersBoard.addColumnModal.addGroup')}
      </button>
    </div>
  );
}
